import React, { useState } from 'react';
import { TextField, Button, MenuItem, Typography, Box, Backdrop, CircularProgress } from '@mui/material';

import { useAlert } from '../../contexts/AlertContext';
import { usePresale } from '../../contexts/PresaleContext';

const communitySizes = [
    'Micro (5k – 15k followers)',
    'Mid (15k – 50k followers)',
    'Large (50k – 150k followers)',
    'Top-tier (150k+ followers)',
];

const communityTypes = [
    'CT Influencer',
    'DAO / Collective',
    'Alpha Group',
    'Other',
];

const PoolSubmissionForm = () => {

    const { showAlert } = useAlert();
    const { isModalLoading, addSubmission } = usePresale();

    const [form, setForm] = useState({
        poolName: '',
        twitter: '',
        wallet: '',
        size: '',
        type: '',
        otherType: '',
        about: '',
        telegram: '',
        notes: '',
    });

    const [errors, setErrors] = useState({
        poolName: false,
        twitter: false,
        wallet: false,
        size: false,
        type: false,
        about: false,
    });

    const handleChange = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
        if (e.target.value) {
            setErrors({ ...errors, [field]: false });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        let isValid = true;

        for (let key of ['poolName', 'twitter', 'wallet', 'size', 'type', 'about']) {
            if (!form[key]) {
                newErrors[key] = true;
                isValid = false;
            }
        }

        setErrors(newErrors);

        if (isValid) { 
            const response = await addSubmission(form);
            if (response.success) {
                showAlert(`Pool submitted successfully! It's now under review.`, 'success');
                setForm({
                    poolName: '',
                    twitter: '',
                    wallet: '',
                    size: '',
                    type: '',
                    otherType: '',
                    about: '',
                    telegram: '',
                    notes: '',
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                showAlert(`Something went wrong. Please try again later.`, 'error');
            }
        }
    };


    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ color: '#fff', backgroundColor: '#121212', padding: 4, borderRadius: 4, maxWidth: 720, margin: 'auto', marginBottom: 20, marginTop: 5 }}>
            <Typography variant="h4" sx={{ color: '#efbc00', mb: 2 }}>Start Your Own Presale Pool</Typography>
            <Typography variant="h6" sx={{ color: '#ccc', mb: 4 }}>
                Become a founding community and earn rewards.
            </Typography>

            <TextField
                fullWidth
                label="Pool Name"
                placeholder="e.g. Solana Frogs United"
                value={form.poolName}
                onChange={handleChange('poolName')}
                helperText="Choose a catchy, meme-worthy name that represents your community."
                FormHelperTextProps={{ style: { color: '#efbc00' } }}
                error={errors.poolName}
                sx={{
                    input: { color: '#fff' },
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
                autoComplete="off"
            />

            <TextField
                fullWidth
                label="Community Twitter (or X)"
                placeholder="@solanafrogs"
                value={form.twitter}
                onChange={handleChange('twitter')}
                helperText="Used to verify your community and link your pool to your Twitter presence."
                FormHelperTextProps={{ style: { color: '#efbc00' } }}
                error={errors.twitter}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
                autoComplete="off"
            />

            <TextField
                fullWidth
                label="Lead Wallet Address"
                placeholder="Enter your Solana wallet address"
                value={form.wallet}
                onChange={handleChange('wallet')}
                helperText="This wallet will receive the 1% Oscar pool reward. Must be a valid Solana address."
                FormHelperTextProps={{ style: { color: '#efbc00' } }}
                error={errors.wallet}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
                autoComplete="off"
            />

            <TextField
                fullWidth
                select
                label="Community Size"
                value={form.size}
                onChange={handleChange('size')}
                error={errors.size}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' },
                    '& .MuiMenuItem-root': { color: '#ccc' },
                    '& .MuiSelect-select': { color: '#ccc' },
                    '& .MuiSelect-icon': { color: '#ccc' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                autoComplete="off"
            >
                {communitySizes.map((size) => (
                    <MenuItem key={size} value={size}>
                        {size}
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                fullWidth
                select
                label="Community Type"
                value={form.type}
                onChange={handleChange('type')}
                error={errors.type}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' },
                    '& .MuiMenuItem-root': { color: '#ccc' },
                    '& .MuiSelect-select': { color: '#ccc' },
                    '& .MuiSelect-icon': { color: '#ccc' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
            >
                {communityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                        {type}
                    </MenuItem>
                ))}
            </TextField>

            {form.type === 'Other' && (
                <TextField
                    fullWidth
                    label="Specify Community Type"
                    value={form.otherType}
                    onChange={handleChange('otherType')}
                    sx={{ mb: 3, backgroundColor: '#1e1e1e' }}
                    InputLabelProps={{ style: { color: '#ccc' } }}
                    InputProps={{ style: { color: '#fff' } }}
                    autoComplete="off"
                />
            )}

            <TextField
                fullWidth
                multiline
                minRows={4}
                label="About Your Community"
                placeholder="Tell us what your community is about, who leads it, and why they’ll ape in."
                value={form.about}
                onChange={handleChange('about')}
                helperText="The more we know, the faster we can approve your pool."
                FormHelperTextProps={{ style: { color: '#efbc00' } }}
                error={errors.about}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
                autoComplete="off"
            />

            <TextField
                fullWidth
                label="Group Chat Link (Optional)"
                placeholder="https://t.me/yourgroup or https://discord.gg/xyz"
                value={form.telegram}
                onChange={handleChange('telegram')}
                helperText="If your community lives outside Twitter, drop the link here."
                FormHelperTextProps={{ style: { color: '#efbc00' } }}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
                autoComplete="off"
            />

            <TextField
                fullWidth
                label="Anything else we should know?"
                placeholder="Custom token ideas? Collab memes? DM for alpha?"
                value={form.notes}
                onChange={handleChange('notes')}
                sx={{
                    mb: 4,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
                autoComplete="off"
            />

            <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                    backgroundColor: '#749d1a',
                    color: '#000',
                    fontWeight: 600,
                    fontSize: 20,
                    padding: '12px 24px',
                    '&:hover': {
                        backgroundColor: '#dbaa00',
                    },
                }}
            >
                Submit Pool for Review
            </Button>

            <Backdrop
                sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1, "position": "absolute" }}
                open={isModalLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>


        </Box>
    );
};

export default PoolSubmissionForm;
